import type { ClientEntitiesSelectedOptions, Criterias } from '@psw/shared';
import { immerable } from 'immer';
import isEqual from 'lodash.isequal';
import { computeClientEntitiesAttributes } from '../Components/ProductFilters/ClientEntities/ClientyEntityFilters.helpers';
import { NumbersUtil } from '../Components/Utils/NumbersUtil';
import type { ListTaxonAttributesTransformedData } from '../Queries/taxon.queries';
import { isEmpty } from '../Utils/object.util';
import type { Catalog } from './Catalog';

export class Filter {
  // To make it works with immer and produce
  [immerable] = true;

  // Use an array in the case of multiple categories in the future, but for now we only have one category
  categories: string[] = [];
  criterias: Criterias = {};
  dateRange: number[] = [0, 0];
  hasDateRangeChanged = false;
  suppliers: string[] = [];
  clientEntities?: ClientEntitiesSelectedOptions;

  /**
   * Build a Filter instance from a form value
   * @param formValue value of the left filter form
   * @returns Filter instance
   */
  static fromFormValue(formValue: IFilterFormData, catalog: Catalog) {
    // Build criterias object
    const criterias: Filter['criterias'] = {};
    Object.entries(formValue.criterias.Accreditations).forEach(([k, v]) => {
      if (v) {
        criterias[Filter.decodeSpecialCharactersForReactHookForm(k)] = ['Yes'];
      }
    });
    Object.entries(formValue.criterias.Allergens).forEach(([k, v]) => {
      if (v) {
        criterias[Filter.decodeSpecialCharactersForReactHookForm(k)] = ['Yes'];
      }
    });
    Object.entries(formValue.criterias.Others).forEach(([k, v]) => {
      const values = Object.entries(v)
        .filter(([k2, v2]) => !!v2)
        .map(([k2, v2]) => Filter.decodeSpecialCharactersForReactHookForm(k2));
      if (values.length > 0) {
        criterias[Filter.decodeSpecialCharactersForReactHookForm(k)] = values as string[] | number[];
      }
    });

    // If some suppliers are selected, set them in the criterias.
    // Why ? Because initially, suppliers filters were displayed in the "Other Criterias" section on the left filters panel, with all others criterias.
    // But as it is an important filter for users, we decided to move it to the top of the left filters panel to make it more prominent.
    // To avoid changing the logic in the Arango queries, we keep the suppliers in the criterias.
    if (formValue.suppliers.length > 0) {
      criterias['Attributes/supplier_name'] = formValue.suppliers;
    }

    const decodedClientEntities = Object.entries(formValue?.clientEntities || {})?.reduce(
      (acc, [key, values]) => ({
        // TODO: fix
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        ...acc,
        [Filter.decodeSpecialCharactersForReactHookForm(key)]: values,
      }),
      {},
    );

    const leftFilters = new Filter();
    leftFilters.categories = formValue.categories;
    leftFilters.criterias = {
      ...criterias,
      ...computeClientEntitiesAttributes(decodedClientEntities, catalog?.client_entity_filters!),
    };
    leftFilters.dateRange = [
      new Date(formValue.dateRange[0]).toArangoDate(),
      new Date(formValue.dateRange[1]).toArangoDate(),
    ];
    leftFilters.hasDateRangeChanged = formValue.hasDateRangeChanged;
    leftFilters.suppliers = formValue.suppliers;

    return leftFilters;
  }

  equals(other: Filter) {
    return (
      isEqual(this.categories, other.categories) &&
      this.dateRange[0] === other.dateRange[0] &&
      this.dateRange[1] === other.dateRange[1] &&
      isEqual(this.criterias, other.criterias) &&
      isEqual(this.suppliers, other.suppliers)
    );
  }

  /**
   * Build a form value from a Filter instance
   */
  toFormData(taxonAttributes: ListTaxonAttributesTransformedData): IFilterFormData {
    const criterias: IFilterFormData['criterias'] = {
      Accreditations: {},
      Allergens: {},
      Others: {},
    };

    // We init all possible taxon attributes to a boolean. By default, React Hook Form init them as undefined.
    // This is fine, but it creates issues when we want to reset the form: nothing is reset because the value is undefined.
    taxonAttributes.accreditations.forEach(k => {
      const value = (this.criterias[k] && this.criterias[k][0] === 'Yes') || false;
      criterias.Accreditations[Filter.encodeSpecialCharactersForReactHookForm(k)] = value;
    });
    taxonAttributes.allergyCompliances.forEach(k => {
      const value = (this.criterias[k] && this.criterias[k][0] === 'Yes') || false;
      criterias.Allergens[Filter.encodeSpecialCharactersForReactHookForm(k)] = value;
    });
    taxonAttributes.filters.forEach(filter => {
      const values: Record<string, boolean> = {};
      if (!filter._id) throw new Error('Filter `_id` is missing');
      if (!Array.isArray(filter.value)) {
        console.error('Filter `value` is missing', { filter });
        return;
      }

      filter.value.forEach(v => {
        let filterValue = false;
        const criteriaValue = this.criterias[filter._id];
        if (criteriaValue) {
          if (typeof v === 'string' && criteriaValue.every(i => typeof i === 'string')) {
            filterValue = criteriaValue.includes(v);
          } else if (typeof v === 'number' && criteriaValue.every(i => typeof i === 'number')) {
            filterValue = criteriaValue.includes(v);
          }
        }
        values[Filter.encodeSpecialCharactersForReactHookForm(v)] = filterValue;
      });
      criterias.Others[Filter.encodeSpecialCharactersForReactHookForm(filter._id)] = values;
    });

    const encodedClientEntities = Object.entries(this.clientEntities || {})?.reduce(
      (acc, [key, values]) => ({
        // TODO: fix
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        ...acc,
        [Filter.encodeSpecialCharactersForReactHookForm(key)]: values,
      }),
      {},
    );

    return {
      categories: this.categories,
      dateRange:
        this.dateRange[0] === 0 || this.dateRange[1] === 0
          ? []
          : [Date.fromArangoDate(this.dateRange[0]), Date.fromArangoDate(this.dateRange[1])],
      hasDateRangeChanged: this.hasDateRangeChanged,
      suppliers: this.suppliers,
      criterias: criterias,
      clientEntities: encodedClientEntities,
      isGlobal: isEmpty(this.clientEntities),
    };
  }

  isEmpty() {
    return (
      (this.categories.length === 0 && this.suppliers.length === 0) ||
      (this.dateRange[0] === 0 && this.dateRange[1] === 0)
    );
  }

  toJSON() {
    return {
      category: this.categories[0],
      criterias: this.criterias,
      dateRange: this.dateRange,
      hasDateRangeChanged: this.hasDateRangeChanged,
      suppliers: this.suppliers,
      clientEntities: this.clientEntities,
    };
  }

  static fromJSON(data: {
    category?: string;
    criterias: Filter['criterias'];
    dateRange: Filter['dateRange'];
    hasDateRangeChanged?: Filter['hasDateRangeChanged'];
    clientEntities?: Filter['clientEntities'];
  }) {
    const leftFilter = new Filter();
    leftFilter.categories = data.category ? [data.category] : [];
    leftFilter.criterias = data.criterias;
    leftFilter.dateRange = data.dateRange;
    leftFilter.hasDateRangeChanged = data.hasDateRangeChanged || false;
    leftFilter.clientEntities = data.clientEntities;

    return leftFilter;
  }

  formatDateRange(separator = '/') {
    return `${NumbersUtil.formatMonthsDateToString(this.dateRange[0], separator)} - ${NumbersUtil.formatMonthsDateToString(this.dateRange[1], separator)}`;
  }

  /**
   * React hook form does not handle values with special characters like ' and ", so we need to encode them.
   * We also need to encode the dot character because it creates weird issues with react hook form with decimal numbers (I guess it's because of the dot notation and how react hook form use it internally)
   */
  static encodeSpecialCharactersForReactHookForm(value: string | number) {
    return `${value}`
      .replaceAll("'", '&apos;')
      .replaceAll('"', '&quot;')
      .replaceAll('.', '&period;')
      .replaceAll(',', '&comma;');
  }

  /**
   * Revert what encodeSpecialCharactersForReactHookForm did and return a number when possible, otherwise a string.
   * This is important to return a number because the app manipulate numbers as numbers. We only cast them to string when using react hook form.
   */
  static decodeSpecialCharactersForReactHookForm(value: string): string | number {
    const newValue = value
      .replaceAll('&apos;', "'")
      .replaceAll('&quot;', '"')
      .replaceAll('&period;', '.')
      .replaceAll('&comma;', ',');
    const valueAsNumber = +newValue;
    if (!Number.isNaN(valueAsNumber)) return valueAsNumber;
    return newValue;
  }
}

export interface IFilterFormData {
  categories: string[];
  dateRange: Date[];
  // true if the user changed the date range and it is not equal to the default 1 year range
  hasDateRangeChanged: boolean;
  suppliers: string[];
  criterias: {
    Accreditations: { [taxonId: string]: boolean };
    Allergens: { [taxonId: string]: boolean };
    Others: { [taxonId: string]: { [taxonValue: string]: boolean } };
  };
  clientEntities?: ClientEntitiesSelectedOptions;
  isGlobal?: boolean;
}
